import type { ColumnDef } from "@tanstack/react-table";
import { Info } from "lucide-react";

import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";

import { LenderUserRoleEnum } from "@/features/common/enums";
import type { ILenderUser } from "@/features/users/types";

import { TeamMemberActions } from "./team-member-actions";
import { TeamMemberRoleCell } from "./team-member-role-cell";

export const columns: ColumnDef<ILenderUser>[] = [
  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => {
      const m = row.original;

      return (
        <span className="font-semibold text-gray-900">
          {m.firstName} {m.lastName}
        </span>
      );
    },
  },
  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => (
      <span className="text-gray-500">
        {row.original.email ?? "—"}
      </span>
    ),
  },
  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    accessorKey: "mobilePhoneNumber",
    header: "Phone",
    cell: ({ row }) => (
      <span className="text-gray-500">
        {row.original.mobilePhoneNumber ?? "—"}
      </span>
    ),
  },
  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    accessorKey: "ssn",
    header: "SSN",
    cell: ({ row }) => (
      <span className="text-gray-500">
        {/* hide last 4 digits */}
        {row.original.ssn ? `${row.original.ssn.slice(0, -4)}****` : "N/A"}
      </span>
    ),
  },

  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    accessorKey: "role",
    header: () => (
      <div className="flex items-center gap-2">
        <span>Role</span>
        <ResponsiveTooltip trigger={<Info className="size-4" />}>
          <div>The role of the user in the lender company.</div>
          <strong>Roles:</strong>
          <ul className="list-inside list-disc">
            <li>
              <strong>{LenderUserRoleEnum.CREDIT_OFFICER}:</strong>
              <ul className="ml-4 list-inside list-disc">
                <li>
                  The user can view applications and perform actions on them.
                </li>
              </ul>
            </li>
            <li>
              <strong>{LenderUserRoleEnum.DEVELOPER}:</strong>
              <ul className="ml-4 list-inside list-disc">
                <li>
                  Everything a user with the{" "}
                  <strong>{LenderUserRoleEnum.CREDIT_OFFICER}</strong> role can
                  do.
                </li>
                <li>
                  The user can view and edit the API configuration and
                  integration logs.
                </li>
              </ul>
            </li>
            <li>
              <strong>{LenderUserRoleEnum.ADMIN}:</strong>
              <ul className="ml-4 list-inside list-disc">
                <li>
                  Everything a user with the{" "}
                  <strong>{LenderUserRoleEnum.DEVELOPER}</strong> role can do.
                </li>
                <li>
                  The user has all permissions in the lender company. The user
                  can view and edit the lender company and settings.
                </li>
                <li>
                  The user can view the team members, invite new users, remove
                  users and edit their roles and basic information.
                </li>
              </ul>
            </li>
          </ul>
        </ResponsiveTooltip>
      </div>
    ),
    cell: ({ row }) => <TeamMemberRoleCell user={row.original} />,
  },
  {
    enableSorting: false,
    enablePinning: false,
    enableResizing: false,
    id: "actions",
    header: () => null,
    cell: ({ row }) => {
      const m = row.original;

      return <TeamMemberActions user={m} />;
    },
  },
];
